import PersonIcon from '@mui/icons-material/Person'
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material'
import { useRecoilValue } from 'recoil'

import { sessionState } from '../../state'

/**
 * UserInfo - Component to display user personal information
 *
 * Displays the user's first name, last name, email, and theme preference.
 * Handles loading and error states for user data.
 */
const UserInfo = () => {
  const session = useRecoilValue(sessionState)
  const { user, isAuthenticating } = session

  // Loading state
  if (isAuthenticating) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            py={4}
          >
            <CircularProgress />
            <Typography variant='body1' sx={{ ml: 2 }}>
              Loading profile information...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Error state - no user data available
  if (!user) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity='error'>
            Unable to load profile information. Please try refreshing the page.
          </Alert>
        </CardContent>
      </Card>
    )
  }

  // Helper function to display user information with fallback
  const displayUserInfo = (
    value: string | null | undefined,
    fallback: string
  ) => {
    return value || fallback
  }

  // Helper function to get theme display name
  const getThemeDisplayName = (theme: string | undefined) => {
    if (!theme) return 'Default'
    return theme === 'dark' ? 'Dark Mode' : 'Light Mode'
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <PersonIcon fontSize='small' />
          </Avatar>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            Profile Information
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            First Name
          </Typography>
          <Typography variant='body1' sx={{ mb: 2 }}>
            {displayUserInfo(user.firstname, 'Not provided')}
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Last Name
          </Typography>
          <Typography variant='body1' sx={{ mb: 2 }}>
            {displayUserInfo(user.lastname, 'Not provided')}
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Email Address
          </Typography>
          <Typography variant='body1' sx={{ mb: 2 }}>
            {displayUserInfo(user.email, 'Not provided')}
          </Typography>

          <Typography variant='body2' color='text.secondary' gutterBottom>
            Theme Preference
          </Typography>
          <Chip
            label={getThemeDisplayName(user.theme)}
            variant='outlined'
            size='small'
            sx={{ mt: 0.5 }}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default UserInfo
